// game/client/src/win-readout.ts
//
// Win-condition standing readout — a compact top-centre strip under the
// day/night clock showing how many players are still alive on each team. The
// match is won by wiping the enemy team (or destroying their Wonder), so
// "enemies still alive" is the single number that tells a player whether they're
// winning or losing — today nothing surfaces it (pre-playtest item #3, 2026-06-25).
//
// Alive counts only by design: the Wonder timer keeps its own HUD (wonder-hud.ts),
// the day count lives in the clock above. Server-authoritative — main.ts computes
// the per-team living counts from room.state.cubes each frame and feeds them here;
// this module only formats. Styles injected once (self-contained, per the
// day-night-clock.ts / wonder-hud.ts pattern).

const STYLE_ID = "win-readout-style";
const CSS = `
.win-readout {
  position: fixed; top: 80px; left: 50%; transform: translateX(-50%);
  z-index: 39; pointer-events: none;
  display: flex; align-items: center; gap: 9px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}
.win-readout.hidden { display: none; }
.wr-team {
  display: flex; align-items: baseline; gap: 6px;
  padding: 3px 11px 4px; border-radius: 6px;
  background: rgba(12, 16, 22, 0.82); backdrop-filter: blur(4px);
  box-shadow: 0 0 10px color-mix(in srgb, var(--wr-color) 26%, transparent);
  border: 1px solid var(--wr-color);
}
.wr-team.ally  { --wr-color: #5bc94a; }
.wr-team.enemy { --wr-color: #f0664f; }
.wr-count {
  font-size: 17px; font-weight: 800; line-height: 1;
  color: var(--wr-color); font-variant-numeric: tabular-nums;
}
.wr-label {
  font-size: 9px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase;
  color: #b8b1a2;
}
.wr-vs { font-size: 10px; font-weight: 700; letter-spacing: 0.08em; color: #6f6a5e; }
`;

export interface WinReadout {
  update(args: { allies: number; enemies: number; show: boolean }): void;
}

export function createWinReadout(): WinReadout {
  if (!document.getElementById(STYLE_ID)) {
    const style = document.createElement("style");
    style.id = STYLE_ID;
    style.textContent = CSS;
    document.head.appendChild(style);
  }

  const root = document.createElement("div");
  root.className = "win-readout hidden";
  root.innerHTML =
    `<div class="wr-team ally">` +
      `<span class="wr-count wr-ally-count">0</span>` +
      `<span class="wr-label">Allies</span>` +
    `</div>` +
    `<span class="wr-vs">VS</span>` +
    `<div class="wr-team enemy">` +
      `<span class="wr-count wr-enemy-count">0</span>` +
      `<span class="wr-label">Enemies</span>` +
    `</div>`;
  document.body.appendChild(root);

  const allyEl = root.querySelector(".wr-ally-count") as HTMLElement;
  const enemyEl = root.querySelector(".wr-enemy-count") as HTMLElement;

  let lastAllies = -1, lastEnemies = -1, lastShow: boolean | null = null;

  function update({ allies, enemies, show }: { allies: number; enemies: number; show: boolean }): void {
    if (show !== lastShow) {
      root.classList.toggle("hidden", !show);
      lastShow = show;
    }
    if (!show) return;
    if (allies !== lastAllies) { allyEl.textContent = String(allies); lastAllies = allies; }
    if (enemies !== lastEnemies) { enemyEl.textContent = String(enemies); lastEnemies = enemies; }
  }

  return { update };
}
